import Cookies from "js-cookie";
import {AdminUserRole, RolesType, UserModel, WebshopRootScope} from "@/models";
import {IAppConfig} from "@commons/constants";
import {SessionInfoService} from "@/Services";
import {normalizeRoles} from "@/Core/roleUtils";

export class GetLoggedInUserConfig {
    static register(app: IModule): void {
        app.run([
            '$rootScope',
            'APP_CONFIG',
            'SessionInfoService',
            (
                $rootScope: WebshopRootScope,
                appConfig: IAppConfig,
                sessionService: SessionInfoService
            ) => {
                const config = new GetLoggedInUserConfig(
                    $rootScope,
                    appConfig,
                    sessionService
                );

                config.initialize();
            }
        ]);
    }

    private constructor(
        private readonly $rootScope: WebshopRootScope,
        private readonly appConfig: IAppConfig,
        private readonly sessionService: SessionInfoService
    ) {
    }

    private initialize(): void {
        this.$rootScope.LoggedInUser = null;
        this.$rootScope.getLoggedInUser = this.getLoggedInUser;
        this.$rootScope.checkUserInRoles = this.checkUserInRoles;
        this.$rootScope.checkUserNotInRoles = this.checkUserNotInRoles;
        this.$rootScope.checkUserInRolesAll = this.checkUserInRolesAll;
        this.$rootScope.checkUserInRolesAllByRoles = this.checkUserInRolesAllByRoles;
    }

    private getLoggedInUser = async (): Promise<UserModel | null> => {
        try {
            const loggedInUser = this.$rootScope.LoggedInUser;

            if (loggedInUser?.['expiredAt']) {
                const expirationDate = new Date(loggedInUser['expiredAt']);

                if (expirationDate.getTime() >= Date.now()) {
                    return loggedInUser;
                }
            }

            const response = await this.sessionService.getInfo();

            if (response?.IsSuccess && response.Value?.Role) {
                const user = response.Value;
                const currentTime = new Date();

                user['fetchedAt'] = currentTime.toISOString();
                user['expiredAt'] = new Date(
                    currentTime.getTime() + this.appConfig.dataTimeToLive * 1000
                ).toISOString();

                this.$rootScope.LoggedInUser = user;
                return user;
            }

            if (response?.Message === 'session_is_expired') {
                Cookies.remove('admin_token');
            }

            this.$rootScope.LoggedInUser = null;
        } catch (error) {
            this.$rootScope.LoggedInUser = null;
            console.error("Auth Error:", error);
            return null;
        } finally {
            this.$rootScope.$applyAsync();
        }

        return this.$rootScope.LoggedInUser;
    }

    private checkUserInRoles = (role: RolesType | RolesType[] | string): boolean => {
        const currentRole = this.$rootScope.LoggedInUser?.Role;

        if (!currentRole || !role) {
            return false;
        }

        if (currentRole === AdminUserRole.SuperAdmin) {
            return true;
        }

        const roles = normalizeRoles(role);
        return roles.includes(AdminUserRole.All) || roles.includes(currentRole);
    };

    private checkUserNotInRoles = (role: RolesType | RolesType[] | string): boolean => {
        const currentRole = this.$rootScope.LoggedInUser?.Role;

        if (!currentRole || !role) {
            return false;
        }

        return !normalizeRoles(role).includes(currentRole);
    };

    private checkUserInRolesAll = (
        roles: RolesType[],
        excludeRoles: RolesType[] = []
    ): boolean => {
        const currentRole = this.$rootScope.LoggedInUser?.Role;

        if (!currentRole || !roles?.length) {
            return false;
        }

        const allowedRoles = normalizeRoles(roles);
        const excludedRoles = normalizeRoles(excludeRoles);

        return !excludedRoles.includes(currentRole) &&
            allowedRoles.includes(currentRole);
    };

    private checkUserInRolesAllByRoles = (
        userRoles: RolesType[],
        allowedRoles: string[] = [],
        excludedRoles: string[] = []
    ): boolean => {
        const normalizedUserRoles = normalizeRoles(userRoles);
        const normalizedAllowedRoles = normalizeRoles(allowedRoles);
        const normalizedExcludedRoles = normalizeRoles(excludedRoles);

        if (!normalizedUserRoles.length) {
            return false;
        }

        if (normalizedUserRoles.some(role => normalizedExcludedRoles.includes(role))) {
            return false;
        }

        if (normalizedAllowedRoles.includes(AdminUserRole.All)) {
            return true;
        }

        if (normalizedUserRoles.includes(AdminUserRole.SuperAdmin)) {
            return true;
        }

        return normalizedUserRoles.some(role => normalizedAllowedRoles.includes(role));
    };
}
